import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { Load } from '../loads/api'

/**
 * A number a dialer takes: the main line's digits, and a rep's extension after a pause —
 * "(555) 104-1728 x404" as DAT prints it. Digits alone would dial 555-104-1728-404.
 */
function telephone(phone: string): string {
  const [main, extension] = phone.split(/\s*(?:x|ext\.?|#)\s*/i)
  const digits = (main ?? '').replace(/[^\d+]/g, '')
  const pause = (extension ?? '').replace(/\D/g, '')
  return `tel:${digits}${pause === '' ? '' : `,${pause}`}`
}

/**
 * How to reach whoever offered the load, one click away and never in the way: the
 * agent the load names; with none — or one the broker has since stood down, kept on the
 * load's record and never offered as a person to call (GPT-5.6 Sol on #35) — the reps
 * DAT listed for the load, who posted it; and with neither, the brokerage's own phone
 * and email. Nothing is sent from here — the dispatcher calls; the platform books nothing.
 */
export function BrokerContact({
  load,
  brokerPhone,
  brokerEmail,
}: {
  load: Load
  brokerPhone: string | null
  brokerEmail: string | null
}) {
  const { t } = useTranslation()
  const [shown, setShown] = useState(false)

  // Only an agent the server says is still in play: a missing answer is not a yes.
  const agentName = load.agentActive === true ? load.agentName : null
  // With no agent to call, the reps DAT listed come before the brokerage's switchboard:
  // they posted the load (the capture from the paste, 2.2).
  const listed = agentName === null ? load.contacts : []
  const name = agentName ?? t('matching.contactBroker', { broker: load.brokerName })
  const phone = agentName === null ? brokerPhone : load.agentPhone
  const email = agentName === null ? brokerEmail : load.agentEmail

  return (
    <div className="match__contact">
      <button
        type="button"
        className="button button--quiet"
        aria-expanded={shown}
        onClick={() => setShown((current) => !current)}
      >
        {t('matching.contact')}
      </button>
      {shown &&
        (listed.length > 0 ? (
          <p>
            <strong>{t('matching.contactDat', { broker: load.brokerName })}</strong>
            {listed.map((contact) => (
              <span key={`${contact.email ?? ''}|${contact.phone ?? ''}`}>
                {contact.phone !== null && (
                  <>
                    {' · '}
                    <a href={telephone(contact.phone)}>{contact.phone}</a>
                  </>
                )}
                {contact.email !== null && (
                  <>
                    {' · '}
                    <a href={`mailto:${contact.email}`}>{contact.email}</a>
                  </>
                )}
              </span>
            ))}
          </p>
        ) : phone === null && email === null ? (
          <p>{t('matching.contactNone', { broker: load.brokerName })}</p>
        ) : (
          <p>
            <strong>{name}</strong>
            {phone !== null && (
              <>
                {' · '}
                <a href={telephone(phone)}>{phone}</a>
              </>
            )}
            {email !== null && (
              <>
                {' · '}
                <a href={`mailto:${email}`}>{email}</a>
              </>
            )}
          </p>
        ))}
    </div>
  )
}
